// src/services/remoteConfig.js
// Firebase Remote Config 연동을 위한 서비스 파일
// ✅ [2025-08-06 수정] 순환 참조 해결 - environment.js import 제거
// ✅ [2025-08-21 추가] 비상 스위치 및 A2U 관련 설정 추가
import { getRemoteConfig, fetchAndActivate, getValue } from 'firebase/remote-config';
import app from '../firebase';

// 로그 제어를 위한 동적 import (순환 참조 방지)
let debugLog = () => { };
let errorLog = console.error;

// 🚀 [수정] URL 파라미터 우선 방식으로 변경
const hasLogAccess = () => {
  try {
    const urlParams = new URLSearchParams(window.location.search);
    const debugParam = urlParams.get('debug');

    if (!debugParam) return false;

    // 🚀 [수정] URL 파라미터만으로 판단 (Remote Config 의존성 제거)
    // 🔒 [보안 강화] 하드코딩된 키 제거 -> 환경변수 사용
    const accessKey = process.env.REACT_APP_LOG_ACCESS_KEY;
    return debugParam === accessKey && !!accessKey;
  } catch (error) {
    return false;
  }
};

// 🚀 [수정] 조건부 로그 함수 직접 구현
debugLog = (...args) => {
  if (hasLogAccess()) {
    console.log(...args);
  }
};

// 비동기로 environment 모듈 로드 시도 (백그라운드)
(async () => {
  try {
    const envModule = await import('../utils/environment');
    debugLog = envModule.debugLog || debugLog;
    errorLog = envModule.errorLog || console.error;
  } catch (error) {
    // import 실패 시 기본 함수 유지
  }
})();

let remoteConfig = null;
let configCache = {};

/**
 * Remote Config 기본값 설정
 * 🚨 보안 주의: 이 파일의 값들은 클라이언트에 노출됩니다
 * - 민감한 토큰이나 비밀키는 절대 포함하지 마세요
 * - 실제 운영값은 Firebase Console에서만 설정하세요
 * - 이 값들은 Firebase 연결 실패 시에만 사용되는 fallback입니다
 */
const defaultConfig = {
  // 게임 시간 설정 (초)
  game_time_default: 60,
  game_time_easy: 90,
  game_time_hard: 45,
  game_time_random: 50,        // 랜덤 플레이 전용 시간
  time_bonus_per_correct: 5,   // 정답 시 추가 시간 (초)

  // 테마 가격 설정 (테스트넷 기준)
  theme_price_masterpiece_2: 0.314,  // [추가] 명화 2 유료화 - 특별 가격
  theme_price_masterpiece_3: 3.14,
  theme_price_masterpiece_4: 3.14,
  theme_price_masterpiece_5: 3.14,
  theme_price_masterpiece_6: 3.14,

  // 구독 가격 설정 (Remote Config 필수 - 기본값 없음)

  // 게임 설정
  hint_count_theme: 3,
  hint_count_random: 5,
  hint_ad_reward: 2,

  // 기능 토글
  random_play_enabled: true,
  ad_enabled: true,
  donation_enabled: true,

  // 🚨 [신규] 비상 스위치 - 긴급 상황 시 즉시 제어
  is_payment_enabled: true,      // 전체 결제 기능 활성화 여부
  is_a2u_enabled: true,          // A2U 전송 기능 활성화 여부
  is_u2a_enabled: true,          // U2A 구매 기능 활성화 여부
  emergency_message: '',         // 비상 상황 메시지

  // 🎁 [신규] Test-Pi 이벤트 설정 (기본값)
  test_pi_event_enabled: false,  // 이벤트 활성화 여부
  test_pi_event_amount: 0.1,     // 지급 금액
  test_pi_event_max_claims: 20,  // 최대 참여자 수
  test_pi_event_per_user: 1,     // 사용자당 최대 받기 횟수
  test_pi_event_start: '',       // 이벤트 시작 시간 (ISO)
  test_pi_event_end: '',         // 이벤트 종료 시간 (ISO)
  test_pi_event_message: '',     // 이벤트 메시지

  // 💰 [신규] A2U 설정
  a2u_min_amount: 0.00000001,    // 최소 전송 금액
  a2u_max_amount: 100,           // 최대 전송 금액
  a2u_daily_limit: 1000,         // 일일 전송 한도
  a2u_per_transaction_limit: 100, // 건당 최대 금액
  a2u_rate_limit_per_hour: 10,   // 시간당 최대 전송 횟수

  // 🔒 [신규] 블록체인 검증 설정
  blockchain_verification_enabled: true,  // 블록체인 검증 활성화
  stellar_network: 'testnet',             // Stellar 네트워크 (testnet/mainnet)
  verification_timeout: 30000,            // 검증 타임아웃 (ms)

  // 🚨 시스템 액세스 설정 (민감한 정보 - Firebase에서만 설정)
  system_auth_key: "",        // 시스템 도구 액세스용 (Firebase Console에서 설정)
  log_access_token: "",       // 로그 액세스용 (Firebase Console에서 설정)

  // 🆕 [2025-12-19 추가] 테스트 모드 설정 (보안 강화)
  test_mode_enabled: false,          // 테스트 모드 전체 비활성화 (기본값)
  test_access_token: "",             // 테스트 모드 접근 토큰 (Firebase Console에서만 설정)
  test_payment_enabled: false,       // 테스트 모드에서만 결제 허용 (기본값)

  // 🆕 [2025-12-19 추가] 기능 제어 설정
  ranking_play_enabled: false,       // 랭킹 플레이 기능 활성화
  season_ranking_enabled: false,     // 시즌 랭킹 기능 활성화
  shop_enabled: false,               // 상점 기능 활성화
  nft_enabled: false,                // NFT 기능 활성화
  menu_enabled: false,               // 메뉴 기능 활성화

  // 🆕 [Ads] 보상형 광고 검증 엄격 모드 플래그
  // - false: 서버 검증(adId) 실패/부분 실패여도 보상 지급 (테스트/승인 대기 단계용)
  // - true : Pi Platform API 검증이 mediated_ack_status === \"granted\" 일 때만 보상 지급
  enable_strict_ad_verification: false
};

/**
 * Firebase Remote Config 초기화
 * - 앱 시작 시 한 번만 호출
 * - 원격 설정을 가져와서 활성화
 * 
 * 🚨 보안 가이드라인:
 * 1. 민감한 토큰은 Firebase Console에서만 설정
 * 2. 클라이언트 코드에는 절대 비밀키 포함 금지
 * 3. 모든 운영값은 Firebase Remote Config로 관리
 * 4. 이 파일의 값들은 fallback용으로만 사용
 */
export const initializeRemoteConfig = async () => {
  try {
    remoteConfig = getRemoteConfig(app);

    // 기본값 설정
    remoteConfig.defaultConfig = defaultConfig;

    // 개발 환경에서는 캐시 시간을 0으로 설정 (즉시 업데이트)
    if (process.env.NODE_ENV === 'development') {
      remoteConfig.settings.minimumFetchIntervalMillis = 0;
    } else {
      // 프로덕션에서는 1시간 캐시
      remoteConfig.settings.minimumFetchIntervalMillis = 3600000;
    }

    // 원격 설정 가져오기 및 활성화
    const activated = await fetchAndActivate(remoteConfig);
    debugLog('[RemoteConfig] 초기화 완료, 활성화:', activated);
    debugLog('[RemoteConfig] 시스템 구성 로드 완료');

    // 모든 값을 캐시에 저장
    Object.keys(defaultConfig).forEach(key => {
      configCache[key] = getValue(remoteConfig, key);
    });

    // 🚨 [신규] 비상 스위치 상태 확인
    const isPaymentEnabled = getConfigBoolean('is_payment_enabled', true);
    const isA2UEnabled = getConfigBoolean('is_a2u_enabled', true);

    if (!isPaymentEnabled || !isA2UEnabled) {
      console.warn('[RemoteConfig] ⚠️ 비상 스위치 활성화됨:', {
        payment: isPaymentEnabled,
        a2u: isA2UEnabled
      });
    }

    // 초기화 성공 표시를 위한 전역 플래그 설정
    window.__remoteConfigInitialized = true;

    return true;
  } catch (error) {
    errorLog('[RemoteConfig] 초기화 실패:', error);
    // Remote Config 실패해도 기본값으로 동작
    return false;
  }
};

/**
 * Remote Config 값 가져오기
 * @param {string} key - 설정 키
 * @returns {object} - 값 접근을 위한 객체 (asString, asNumber, asBoolean 메서드 포함)
 */
export const getConfigValue = (key) => {
  // 캐시에서 먼저 확인
  if (configCache[key]) {
    return configCache[key];
  }

  // Remote Config에서 가져오기
  if (remoteConfig) {
    try {
      const value = getValue(remoteConfig, key);
      configCache[key] = value;
      return value;
    } catch (error) {
      errorLog(`[RemoteConfig] 값 가져오기 실패 (${key}):`, error);
    }
  }

  // Remote Config를 사용할 수 없으면 기본값 반환
  const defaultValue = defaultConfig[key];
  return {
    asString: () => String(defaultValue || ''),
    asNumber: () => Number(defaultValue || 0),
    asBoolean: () => Boolean(defaultValue || false),
    // 추가: JSON 파싱을 위한 메서드
    asJSON: () => {
      try {
        return typeof defaultValue === 'string' ? JSON.parse(defaultValue) : defaultValue;
      } catch {
        return null;
      }
    }
  };
};

/**
 * Remote Config 값을 숫자로 가져오기 (편의 함수)
 * @param {string} key - 설정 키
 * @param {number} defaultValue - 기본값
 * @returns {number}
 */
export const getConfigNumber = (key, defaultValue = 0) => {
  try {
    return getConfigValue(key).asNumber();
  } catch (error) {
    errorLog(`[RemoteConfig] 숫자 변환 실패 (${key}):`, error);
    return defaultValue;
  }
};

/**
 * Remote Config 값을 문자열로 가져오기 (편의 함수)
 * @param {string} key - 설정 키
 * @param {string} defaultValue - 기본값
 * @returns {string}
 */
export const getConfigString = (key, defaultValue = '') => {
  try {
    return getConfigValue(key).asString();
  } catch (error) {
    errorLog(`[RemoteConfig] 문자열 변환 실패 (${key}):`, error);
    return defaultValue;
  }
};

/**
 * Remote Config 값을 불린으로 가져오기 (편의 함수)
 * @param {string} key - 설정 키
 * @param {boolean} defaultValue - 기본값
 * @returns {boolean}
 */
export const getConfigBoolean = (key, defaultValue = false) => {
  try {
    return getConfigValue(key).asBoolean();
  } catch (error) {
    errorLog(`[RemoteConfig] 불린 변환 실패 (${key}):`, error);
    return defaultValue;
  }
};

/**
 * Remote Config 값을 JSON으로 가져오기 (편의 함수)
 * @param {string} key - 설정 키
 * @param {object} defaultValue - 기본값
 * @returns {object}
 */
export const getConfigJSON = (key, defaultValue = null) => {
  try {
    const configValue = getConfigValue(key);
    const jsonString = configValue.asString();
    if (!jsonString) return defaultValue;
    return JSON.parse(jsonString);
  } catch (error) {
    errorLog(`[RemoteConfig] JSON 파싱 실패 (${key}):`, error);
    return defaultValue;
  }
};

// ==================== [신규] 시즌 랭킹 시스템 ====================

/**
 * 그림팩 번호를 테마 ID로 변환하는 매핑 테이블
 */
const THEME_MAPPING = {
  1: "masterpiece1",    // 명화 1
  2: "masterpiece2",    // 명화 2  
  3: "masterpiece3",    // 명화 3
  4: "masterpiece4",    // 명화 4
  5: "masterpiece5",    // 명화 5
  6: "masterpiece6"     // 명화 6
};

/**
 * 한국시간을 UTC로 변환
 * @param {string} kstTimeString - "2024-10-15 14:00" 형식의 한국시간
 * @returns {string} ISO 형식의 UTC 시간
 */
export const convertKSTToUTC = (kstTimeString) => {
  try {
    // 한국시간은 UTC+9 고정 (DST 없음)
    const kstDate = new Date(kstTimeString + '+09:00');
    return kstDate.toISOString();
  } catch (error) {
    errorLog('[RemoteConfig] 한국시간 변환 실패:', error);
    return new Date().toISOString();
  }
};

/**
 * 사용자 지역별 시간 표시 (시간대 약어 포함)
 * @param {string} utcTime - UTC 시간
 * @returns {string} 지역화된 시간 문자열
 */
export const getLocalizedTimeDisplay = (utcTime) => {
  try {
    const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const userLocale = navigator.language || 'en-US';
    const date = new Date(utcTime);

    // 사용자 언어에 맞는 지역화된 시간 포맷
    const localTime = new Intl.DateTimeFormat(userLocale, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: userTimezone
    }).format(date);

    // 시간대 약어 추출
    const timeZoneAbbr = new Intl.DateTimeFormat('en-US', {
      timeZoneName: 'short',
      timeZone: userTimezone
    }).formatToParts(date).find(part => part.type === 'timeZoneName')?.value || 'UTC';

    return `Opens: ${localTime} ${timeZoneAbbr}`;
  } catch (error) {
    errorLog('[RemoteConfig] 시간 지역화 실패:', error);
    return `Opens: ${new Date(utcTime).toLocaleString()}`;
  }
};

// 🚀 [성능 최적화] 시즌 설정 캐시 - 5분 TTL
const SEASON_CONFIG_CACHE = {
  data: null,
  timestamp: 0,
  TTL: 5 * 60 * 1000, // 5분
  isLoading: false // 동시 접근 방지
};

/**
 * 시즌 랭킹 설정 가져오기 (메모이제이션 적용)
 * @param {boolean} forceRefresh - 강제 새로고침 여부
 * @returns {object} 시즌 설정 객체
 */
export const getSeasonConfig = (forceRefresh = false) => {
  const defaultConfig = {
    enabled: false,
    round: 1,
    startTime: new Date().toISOString(),
    targetThemes: 1
  };

  const now = Date.now();

  // 🚀 [수정] 동시 접근 방지 - 로딩 중이면 기존 데이터 반환
  if (SEASON_CONFIG_CACHE.isLoading && SEASON_CONFIG_CACHE.data) {
    debugLog('[RemoteConfig] 시즌 설정 로딩 중 - 기존 데이터 반환');
    return SEASON_CONFIG_CACHE.data;
  }

  // 캐시가 유효하고 강제 새로고침이 아닌 경우 캐시 반환
  if (!forceRefresh && SEASON_CONFIG_CACHE.data &&
    (now - SEASON_CONFIG_CACHE.timestamp) < SEASON_CONFIG_CACHE.TTL) {
    debugLog('[RemoteConfig] 시즌 설정 캐시 히트');
    return SEASON_CONFIG_CACHE.data;
  }

  // 🚀 [수정] 동시 접근 방지 - 로딩 플래그 설정
  SEASON_CONFIG_CACHE.isLoading = true;

  try {
    // 🚀 [수정] getConfigJSON 대신 직접 Remote Config에서 가져오기
    // 이중 캐시 문제 해결을 위해 configCache를 우회
    let configData;

    if (remoteConfig) {
      try {
        const configValue = getValue(remoteConfig, 'season_ranking_config');
        const jsonString = configValue.asString();

        if (jsonString) {
          configData = JSON.parse(jsonString);
        } else {
          configData = defaultConfig;
        }
      } catch (error) {
        errorLog('[RemoteConfig] 시즌 설정 직접 로드 실패:', error);
        configData = defaultConfig;
      }
    } else {
      configData = defaultConfig;
    }

    debugLog('[DEBUG] Raw season config from Remote Config:', configData);

    // startTime이 한국시간 형식이면 UTC로 변환
    if (configData.startTime && !configData.startTime.includes('T')) {
      const originalTime = configData.startTime;
      configData.startTime = convertKSTToUTC(configData.startTime);
      debugLog('[DEBUG] Time conversion:', { original: originalTime, converted: configData.startTime });
    }

    // 캐시에 저장
    SEASON_CONFIG_CACHE.data = configData;
    SEASON_CONFIG_CACHE.timestamp = now;
    SEASON_CONFIG_CACHE.isLoading = false;

    debugLog('[RemoteConfig] 시즌 설정 로드:', configData);
    debugLog('[DEBUG] Final season config:', configData);
    return configData;
  } catch (error) {
    debugLog('[DEBUG] 시즌 설정 로드 실패:', error);
    errorLog('[RemoteConfig] 시즌 설정 로드 실패:', error);
    SEASON_CONFIG_CACHE.isLoading = false;
    return defaultConfig;
  }
};

/**
 * 시즌 활성 상태 확인
 * @param {object} config - 시즌 설정 객체
 * @returns {boolean} 시즌 활성 여부
 */
export const isSeasonActive = (config) => {
  if (!config || !config.enabled) return false;

  const now = new Date();
  const startTime = new Date(config.startTime);
  const endTime = new Date(startTime.getTime() + (7 * 24 * 60 * 60 * 1000)); // 7일 후

  return now >= startTime && now < endTime;
};

/**
 * 시즌 종료 여부 확인
 * @param {object} config - 시즌 설정 객체
 * @returns {boolean} 시즌 종료 여부
 */
export const isSeasonEnded = (config) => {
  if (!config || !config.enabled) return false;

  const now = new Date();
  const startTime = new Date(config.startTime);
  const endTime = new Date(startTime.getTime() + (7 * 24 * 60 * 60 * 1000)); // 7일 후

  return now >= endTime;
};

/**
 * 시즌 표시 정보 생성
 * @param {object} config - 시즌 설정 객체
 * @returns {object} UI 표시 정보
 */
export const getSeasonDisplayInfo = (config) => {
  debugLog('[DEBUG] getSeasonDisplayInfo called with config:', config);

  if (!config || !config.enabled) {
    debugLog('[DEBUG] Season disabled or no config');
    return {
      status: 'disabled',
      display: '',
      showButton: true, // 🔧 [수정] 버튼은 항상 표시
      buttonEnabled: false // 🔧 [수정] 비활성화 상태로 표시
    };
  }

  const now = new Date();
  const startTime = new Date(config.startTime);
  const endTime = new Date(startTime.getTime() + (7 * 24 * 60 * 60 * 1000)); // 7일 후

  debugLog('[DEBUG] Time comparison:', {
    now: now.toISOString(),
    startTime: startTime.toISOString(),
    endTime: endTime.toISOString(),
    nowTime: now.getTime(),
    startTimeTime: startTime.getTime(),
    endTimeTime: endTime.getTime()
  });

  if (now < startTime) {
    // 시작 전: 오픈 시간 표시
    debugLog('[DEBUG] Season upcoming');
    return {
      status: 'upcoming',
      display: getLocalizedTimeDisplay(config.startTime),
      showButton: true,
      buttonEnabled: false,
      seasonId: `SEASON-${config.round.toString().padStart(2, '0')}`
    };
  } else if (now >= startTime && now < endTime) {
    // 진행 중: 남은 시간 표시 (다국어 형식)
    const timeLeft = endTime - now;
    const days = Math.floor(timeLeft / (1000 * 60 * 60 * 24));
    const hours = Math.floor((timeLeft % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((timeLeft % (1000 * 60 * 60)) / (1000 * 60));

    debugLog('[DEBUG] Season active:', { days, hours, minutes });
    return {
      status: 'active',
      display: { days, hours, minutes }, // 객체로 반환하여 컴포넌트에서 다국어 처리
      showButton: true,
      buttonEnabled: true,
      seasonId: `SEASON-${config.round.toString().padStart(2, '0')}`
    };
  } else {
    // 종료: 숨김
    debugLog('[DEBUG] Season ended');
    return {
      status: 'ended',
      display: '',
      showButton: false,
      buttonEnabled: false
    };
  }
};

/**
 * 대상 그림팩 ID 가져오기
 * @param {number} themeNumber - 그림팩 번호 (1-6)
 * @returns {string} 테마 ID
 */
export const getTargetThemeId = (themeNumber) => {
  return THEME_MAPPING[themeNumber] || null;
};

/**
 * 시즌 ID 생성
 * @param {number} round - 회차 번호
 * @returns {string} 시즌 ID
 */
export const generateSeasonId = (round) => {
  return `SEASON-${round.toString().padStart(2, '0')}`;
};

/**
 * 기능 활성화 여부 확인 (테스트 모드 고려)
 * @param {string} featureKey - 기능 키 (예: 'ranking_play_enabled')
 * @param {boolean} isTestMode - 테스트 모드 여부
 * @returns {boolean}
 */
export const isFeatureEnabled = (featureKey, isTestMode = false) => {
  // 테스트 모드에서는 항상 활성화
  if (isTestMode) {
    return true;
  }

  // 일반 모드에서는 Remote Config 값 확인
  return getConfigBoolean(featureKey, false);
};

/**
 * 🚨 [신규] 비상 스위치 상태 확인
 * @returns {object} 비상 스위치 상태
 */
export const getEmergencyStatus = () => {
  return {
    isPaymentEnabled: getConfigBoolean('is_payment_enabled', true),
    isA2UEnabled: getConfigBoolean('is_a2u_enabled', true),
    isU2AEnabled: getConfigBoolean('is_u2a_enabled', true),
    emergencyMessage: getConfigString('emergency_message', ''),
    isEmergency: !getConfigBoolean('is_payment_enabled', true) ||
      !getConfigBoolean('is_a2u_enabled', true)
  };
};

/**
 * 🚨 [신규] 보안 체크 함수
 * 민감한 정보가 클라이언트에 노출되지 않았는지 확인
 * @returns {object} 보안 상태
 */
export const getSecurityStatus = () => {
  const systemAuthKey = getConfigString('system_auth_key', '');
  const logAccessToken = getConfigString('log_access_token', '');
  const testAccessToken = getConfigString('test_access_token', '');

  return {
    hasSystemAuthKey: !!systemAuthKey,
    hasLogAccessToken: !!logAccessToken,
    hasTestAccessToken: !!testAccessToken,
    isSecure: !!(systemAuthKey && logAccessToken && testAccessToken),
    warning: !systemAuthKey || !logAccessToken || !testAccessToken
      ? '민감한 토큰들이 Firebase Console에서 설정되지 않았습니다.'
      : null
  };
};

/**
 * 🎁 [신규] Test-Pi 이벤트 설정 가져오기
 * @returns {object} 이벤트 설정
 */
export const getTestPiEventConfig = () => {
  const now = new Date();
  const startTime = getConfigString('test_pi_event_start');
  const endTime = getConfigString('test_pi_event_end');

  let isActive = getConfigBoolean('test_pi_event_enabled', false);

  // 시간 체크
  if (startTime && new Date(startTime) > now) {
    isActive = false; // 아직 시작 안 함
  }
  if (endTime && new Date(endTime) < now) {
    isActive = false; // 이미 종료됨
  }

  return {
    enabled: getConfigBoolean('test_pi_event_enabled', false),
    isActive,
    amount: getConfigNumber('test_pi_event_amount', 0.1),
    maxClaims: getConfigNumber('test_pi_event_max_claims', 20),
    perUser: getConfigNumber('test_pi_event_per_user', 1),
    startTime,
    endTime,
    message: getConfigString('test_pi_event_message', '')
  };
};

/**
 * 💰 [신규] A2U 설정 가져오기
 * @returns {object} A2U 설정
 */
export const getA2UConfig = () => {
  return {
    enabled: getConfigBoolean('is_a2u_enabled', true),
    minAmount: getConfigNumber('a2u_min_amount', 0.00000001),
    maxAmount: getConfigNumber('a2u_max_amount', 100),
    dailyLimit: getConfigNumber('a2u_daily_limit', 1000),
    perTransactionLimit: getConfigNumber('a2u_per_transaction_limit', 100),
    rateLimitPerHour: getConfigNumber('a2u_rate_limit_per_hour', 10),
    blockchainVerification: getConfigBoolean('blockchain_verification_enabled', true),
    stellarNetwork: getConfigString('stellar_network', 'testnet'),
    verificationTimeout: getConfigNumber('verification_timeout', 30000)
  };
};

/**
 * Remote Config 강제 새로고침
 * - 캐시를 무시하고 최신 값을 가져옴
 * - 관리자 기능이나 특별한 경우에만 사용
 */
export const refreshRemoteConfig = async () => {
  if (!remoteConfig) {
    debugLog('[RemoteConfig] Remote Config가 초기화되지 않음');
    return false;
  }

  try {
    // 캐시 초기화 (전체 캐시)
    configCache = {};
    // 🚀 [수정] 시즌 설정 캐시도 완전 초기화
    SEASON_CONFIG_CACHE.data = null;
    SEASON_CONFIG_CACHE.timestamp = 0;
    SEASON_CONFIG_CACHE.isLoading = false;

    // 최신 설정 가져오기
    const activated = await fetchAndActivate(remoteConfig);
    debugLog('[RemoteConfig] 새로고침 완료, 활성화:', activated);

    // 캐시 재구성
    Object.keys(defaultConfig).forEach(key => {
      configCache[key] = getValue(remoteConfig, key);
    });

    // 🚨 [신규] 비상 스위치 상태 재확인
    const emergencyStatus = getEmergencyStatus();
    if (emergencyStatus.isEmergency) {
      console.warn('[RemoteConfig] ⚠️ 비상 모드 활성화:', emergencyStatus);

      // 전역 알림 설정 (필요시)
      window.__EMERGENCY_MODE__ = emergencyStatus;
    }

    return true;
  } catch (error) {
    errorLog('[RemoteConfig] 새로고침 실패:', error);
    return false;
  }
};

/**
 * 현재 Remote Config 상태 확인 (디버깅용)
 */
export const getRemoteConfigStatus = () => {
  const emergencyStatus = getEmergencyStatus();
  const testPiEvent = getTestPiEventConfig();
  const a2uConfig = getA2UConfig();

  return {
    initialized: !!remoteConfig,
    cacheSize: Object.keys(configCache).length,
    lastFetchTime: remoteConfig?.lastFetchTime || null,
    systemConfigured: !!getConfigString('system_auth_key') && !!getConfigString('log_access_token'),
    // 🚨 [신규] 추가 상태 정보
    emergency: emergencyStatus,
    testPiEvent: testPiEvent,
    a2u: {
      enabled: a2uConfig.enabled,
      limits: {
        min: a2uConfig.minAmount,
        max: a2uConfig.maxAmount,
        daily: a2uConfig.dailyLimit
      }
    }
  };
};